<template>
  <div class="course-section">
    <!-- Header -->
    <div class="section-header">
      <div class="section-title">
        <span class="title-bar"></span>
        <span class="title-text">课程购买<span class="title-tip">(选择课程后请点击支付)</span></span>
      </div>
    </div>

    <!-- 专业 Tab -->
    <div class="major-tabs">
      <span
        v-for="tab in majorTabs"
        :key="tab.value"
        :class="['major-tab', { active: activeTab === tab.value }]"
        @click="activeTab = tab.value"
      >{{ tab.label }}</span>
    </div>

    <!-- 购买课程列表（按专业分散到各 Tab） -->
    <div class="course-grid-wrap">
      <div class="buy-list" v-if="currentBuyList.length > 0">
        <div
          class="buy-item"
          v-for="item in currentBuyList"
          :key="item.id"
          @click="toggleCheck(item)"
        >
          <div class="item-cover" :style="coverStyle(item)">
            <!-- 背景图案：流动曲线光带 -->
            <svg class="cover-wave" viewBox="0 0 400 130" preserveAspectRatio="none" fill="none">
              <path d="M0,96 C96,34 168,116 262,64 C330,26 372,74 400,52" stroke="rgba(255,255,255,0.38)" stroke-width="1.6" />
              <path d="M0,112 C96,54 176,126 268,78 C334,42 378,88 400,70" stroke="rgba(255,255,255,0.22)" stroke-width="1.1" />
              <path d="M0,74 C104,18 186,92 276,46 C336,14 382,56 400,36" stroke="rgba(255,255,255,0.14)" stroke-width="1.1" />
            </svg>
            <span v-if="hoursOf(item)" class="cover-badge">{{ hoursOf(item) }}学时</span>
            <span class="cover-name">{{ item.cateName }}</span>
          </div>
          <div class="item-content">
            <span class="price"><i>¥</i>{{ formatPrice(item.price) }}</span>
            <div class="item-right">
              <span v-if="item.isPay === 1" class="paid">已购买</span>
              <span v-else class="to-buy">未购买</span>
              <el-checkbox
                v-model="item.checked"
                :disabled="item.isPay === 1"
                @click.native.stop
                @change="handleChecked"
              ></el-checkbox>
            </div>
          </div>
        </div>
      </div>
      <div class="course-empty" v-else>
        <p>暂无可购买课程</p>
      </div>
    </div>

    <!-- 确认订单 -->
    <div class="buy-btn" v-if="checkedList.length > 0">
      <el-button class="btn-order" @click="createOrder">确认订单</el-button>
    </div>

    <payModal ref="payModal"></payModal>
  </div>
</template>

<script>
import { getMyCategory } from "@/api/course";
import { createOrder } from "@/api/order";
import payModal from "@/views/payModal";

// 各专业关键词（用于把购买列表按专业分散到对应 Tab）
const MAJOR_KEYS = ["建筑", "市政", "机电", "公路", "水利", "矿业"];

// 各专业的卡片配色（参考 ljzcjy 教材封面：明亮品牌色渐变）
const MAJOR_THEMES = [
  { key: "建筑", bg: "linear-gradient(135deg, #6fa8e6 0%, #2a63b5 100%)" },
  { key: "市政", bg: "linear-gradient(135deg, #63c3d6 0%, #1f83a6 100%)" },
  { key: "机电", bg: "linear-gradient(135deg, #a58ce0 0%, #6141b0 100%)" },
  { key: "公路", bg: "linear-gradient(135deg, #8bc98c 0%, #3f8f4d 100%)" },
  { key: "水利", bg: "linear-gradient(135deg, #7fc9f2 0%, #2c86c8 100%)" },
  { key: "矿业", bg: "linear-gradient(135deg, #e0a271 0%, #a85f2c 100%)" },
];
// 未识别专业时的默认配色
const DEFAULT_THEME = { bg: "linear-gradient(135deg, #8aa0bb 0%, #44618a 100%)" };

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: "index",
  components: { payModal },
  props: {
    categories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeTab: "",
      categoryList: [],
      checkedList: [],
    };
  },
  computed: {
    majorTabs() {
      const tabs = [];
      this.categories.forEach((cat) => {
        if (cat.cateName) {
          tabs.push({ label: cat.cateName, value: cat.cateName });
        }
      });
      // 建筑放在第一个
      tabs.sort((a, b) => {
        if (a.label.indexOf("建筑") !== -1) return -1;
        if (b.label.indexOf("建筑") !== -1) return 1;
        return 0;
      });
      return tabs;
    },
    // 当前 Tab 对应的购买课程
    currentBuyList() {
      const key = this.majorKeyOf(this.activeTab);
      if (!key) return [];
      return this.categoryList.filter((item) => (item.cateName || "").indexOf(key) !== -1);
    },
  },
  created() {
    this.getMyCategory();
  },
  watch: {
    majorTabs: {
      immediate: true,
      handler(tabs) {
        if (tabs.length > 0 && !this.activeTab) {
          this.activeTab = tabs[0].value;
        }
      },
    },
  },
  methods: {
    // 取专业关键词
    majorKeyOf(name) {
      return MAJOR_KEYS.find((k) => (name || "").indexOf(k) !== -1) || name || "";
    },
    // 按专业取卡片背景
    themeOf(item) {
      const name = item.cateName || "";
      return MAJOR_THEMES.find((t) => name.indexOf(t.key) !== -1) || DEFAULT_THEME;
    },
    // 价格显示：保留两位小数
    formatPrice(price) {
      const n = Number(price);
      if (isNaN(n)) return price;
      return n.toFixed(2);
    },
    // 增项 30 学时，主项 60 学时
    hoursOf(item) {
      const name = item.cateName || "";
      if (name.indexOf("增项") !== -1) return 30;
      if (name.indexOf("主项") !== -1) return 60;
      return "";
    },
    coverStyle(item) {
      const t = this.themeOf(item);
      return {
        // 右上角柔光 + 专业渐变
        background:
          "radial-gradient(circle at 78% 12%, rgba(255, 255, 255, 0.34) 0%, rgba(255, 255, 255, 0) 58%), " +
          t.bg,
      };
    },
    // 点击整张卡片切换选中状态
    toggleCheck(item) {
      if (item.isPay === 1) return;
      this.$set(item, "checked", !item.checked);
      this.handleChecked();
    },
    handleChecked() {
      this.checkedList = [];
      this.categoryList.forEach((item) => {
        if (item.checked === true) this.checkedList.push(item.id);
      });
    },
    async createOrder() {
      try {
        if (this.checkedList.length === 0) {
          this.$message({ type: "warning", message: "请选择课程！" });
          return;
        }
        const res = await createOrder({ categoryIds: this.checkedList.join(",") });
        this.$refs.payModal.open(res.data);
      } catch (error) {}
    },
    async getMyCategory() {
      try {
        const res = await getMyCategory({});
        this.categoryList = res.data;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.course-section {
  padding: 24px;
  background: #fff;
  border-radius: 2px;
  border: 1px solid #e3e8ef;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;

  .section-title {
    display: flex;
    align-items: center;

    .title-bar {
      width: 4px;
      height: 20px;
      background: #1c4e8a;
      border-radius: 0;
      margin-right: 10px;
    }

    .title-text {
      font-size: 20px;
      font-weight: 700;
      color: #222;

      .title-tip {
        color: #e8821e;
      }
    }
  }
}

// 专业 Tab
.major-tabs {
  display: flex;
  gap: 0;
  margin: 20px 0 16px;
  border-bottom: 2px solid #e8eaed;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  .major-tab {
    padding: 12px 24px;
    font-size: 17px;
    font-weight: 500;
    color: #333;
    cursor: pointer;
    position: relative;
    white-space: nowrap;
    transition: color 0.2s;

    &:hover {
      color: #1c4e8a;
    }

    &.active {
      color: #1c4e8a;
      font-weight: 700;
      font-size: 18px;

      &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 3px;
        background: #1c4e8a;
      }
    }
  }
}

.course-grid-wrap {
  min-height: 160px;
}

// ====================== 购买课程卡片 ======================
.buy-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.buy-item {
  min-width: 0;
  background: #fff;
  border: 1px solid #e3e8ef;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.25s;

  &:hover {
    box-shadow: 0 4px 16px rgba(20, 40, 80, 0.12);
  }

  // 封面：背景图案 + 文字
  .item-cover {
    position: relative;
    height: 118px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: linear-gradient(135deg, #6fa8e6 0%, #2a63b5 100%);

    .cover-wave {
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
    }

    // 左上角学时标记
    .cover-badge {
      position: absolute;
      top: 8px;
      left: 8px;
      z-index: 3;
      padding: 0 10px;
      font-size: 16px;
      line-height: 24px;
      color: #fff;
      font-weight: 600;
      background: #1c4e8a;
      border-radius: 2px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
      white-space: nowrap;
    }

    .cover-name {
      position: relative;
      z-index: 2;
      padding: 0 14px;
      font-size: 20px;
      font-weight: 600;
      color: #fff;
      text-align: center;
      line-height: 1.5;
      word-break: break-all;
      text-shadow: 0 1px 4px rgba(23, 48, 92, 0.3);
    }
  }

  .item-content {
    padding: 12px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 15px;

    .price {
      color: #e8821e;
      font-size: 20px;
      font-weight: 700;
      line-height: 1;

      i {
        font-style: normal;
        font-size: 14px;
        margin-right: 1px;
      }
    }

    .item-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .paid {
      color: #1c4e8a;
      font-weight: 500;
    }

    .to-buy {
      color: #8a94a6;
    }
  }
}

.buy-btn {
  padding: 20px 0 0;
  text-align: right;

  .btn-order {
    background: linear-gradient(135deg, #FF8F00, #F57C00);
    border: none;
    color: #fff;
    font-weight: 600;
    padding: 10px 28px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(245, 124, 0, 0.3);
    transition: all 0.3s;

    &:hover {
      background: linear-gradient(135deg, #F57C00, #E65100);
      box-shadow: 0 4px 14px rgba(245, 124, 0, 0.4);
      color: #fff;
    }
  }
}

.course-empty {
  padding: 60px 0;
  text-align: center;
  color: #bbb;
  font-size: 14px;
}

@media (max-width: 768px) {
  .buy-list {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .major-tabs {
    overflow-x: auto;
  }

  .major-tab {
    padding: 10px 18px;
    font-size: 15px;
    flex-shrink: 0;
  }
}
</style>
